import { onAuthStateChanged } from 'firebase/auth'
import { firebaseAuth } from './firebase'
import { callable } from './callable'

export const FLAGS_CACHE_KEY = 'tl_flags_cache_v1'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export interface FeatureFlagCache {
  uid: string
  flags: Record<string, boolean>
  evaluatedAt: number
  fetchedAt: number
}

export interface GetMyFlagsResponse {
  flags: Record<string, boolean>
  evaluatedAt: number
}

export const readFlagCache = (): FeatureFlagCache | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(FLAGS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as FeatureFlagCache
    if (!parsed || typeof parsed !== 'object' || typeof parsed.flags !== 'object' || parsed.flags === null) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const writeFlagCache = (cache: FeatureFlagCache): void => {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(cache))
  } catch {
    // Ignore storage quota / access errors
  }
}

export const clearFlagCache = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(FLAGS_CACHE_KEY)
  } catch {
    // Ignore storage access errors
  }
}

export const fetchMyFlags = async (): Promise<GetMyFlagsResponse> => {
  return callable<Record<string, never>, GetMyFlagsResponse>(
    'getMyFlags',
    {},
    { timeoutMs: 10_000 },
  )
}

export const readCachedFlag = (name: string): boolean | undefined => {
  const cache = readFlagCache()
  if (!cache || !cache.flags) return undefined
  const val = cache.flags[name]
  return typeof val === 'boolean' ? val : undefined
}

let isSyncStarted = false
let unsubscribeAuth: (() => void) | null = null
let visibilityHandler: (() => void) | null = null

export const startFeatureFlagSync = (): void => {
  if (isSyncStarted) return
  isSyncStarted = true

  const performFetch = async (uid: string) => {
    try {
      const response = await fetchMyFlags()
      writeFlagCache({
        uid,
        flags: response.flags,
        evaluatedAt: response.evaluatedAt,
        fetchedAt: Date.now(),
      })
    } catch (error) {
      console.warn('[featureFlagsService] Failed to fetch flags:', error)
    }
  }

  try {
    unsubscribeAuth = onAuthStateChanged(firebaseAuth, async (user) => {
      if (user && !user.isAnonymous) {
        await performFetch(user.uid)
      } else {
        clearFlagCache()
      }
    })
  } catch (error) {
    console.warn('[featureFlagsService] Failed to bind auth listener:', error)
  }

  if (typeof document !== 'undefined') {
    visibilityHandler = async () => {
      if (document.visibilityState !== 'visible') return
      const user = firebaseAuth.currentUser
      if (!user || user.isAnonymous) return

      const cache = readFlagCache()
      const isExpired = !cache || (Date.now() - cache.fetchedAt >= CACHE_TTL_MS)
      if (isExpired) {
        await performFetch(user.uid)
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)
  }
}

export const _resetSyncForTesting = (): void => {
  if (unsubscribeAuth) {
    unsubscribeAuth()
    unsubscribeAuth = null
  }
  if (visibilityHandler && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  isSyncStarted = false
}
